/**
 * Time to Connect — the Grandstream-only KPI on Alert Details and Hourly Summary
 * (docs/TIME_TO_CONNECT_PLAN.md).
 *
 * The report Lambda computes it (attachTimeToConnect in serverless/mysql_report.py)
 * and puts two keys on each row:
 *   time_to_connect  minutes to the first ANSWERED intercom call, null without one
 *   intercom_used    the alert had a PLACED intercom call (answered, no answer or busy)
 * plus `avg_time_to_connect` (alerts with an answered call only, null when none) and
 * `intercom_usage_pct` (alerts with a placed call / all alerts) on the summary. The SQL
 * definition is generated by scripts/gen_time_to_connect_procs.py (phase 4, 2026-10-16).
 * This file only holds what the page shows about it.
 *
 * Operations rule (2026-10-09): alerts without one are left out of the average and
 * shown as text, never 0 and never their response time. Phase 4 splits that text:
 * "No answer" when an intercom call was placed but nobody answered, N/A when there was
 * no intercom call at all.
 */

/** Columns that exist only for Grandstream campuses (core_campus.is_grandstream). */
export const GRANDSTREAM_ONLY_COLUMNS = ['calls', 'time_to_connect'];

/** What an alert with no intercom call shows for Time to Connect. */
export const TIME_TO_CONNECT_NA = 'N/A';

/** What an alert whose intercom call(s) nobody answered shows for Time to Connect. */
export const TIME_TO_CONNECT_NO_ANSWER = 'No answer';

export const TIME_TO_CONNECT_INFO =
    'Time from the alert until the first intercom call to the resident or their room was answered. '
    + 'Only alerts with an answered intercom call are included. Alerts where the intercom was used '
    + 'but nobody answered show No answer, alerts without an intercom call show N/A, and both are '
    + 'left out of the average.';

export const INTERCOM_USAGE_INFO =
    'Share of all alerts where an intercom call was placed, answered or not. Counted per alert, so '
    + 'several calls on one alert count once.';

const hasMinutes = (value) => value != null && value !== '';

/**
 * What a row shows for Time to Connect: the minutes, "No answer", or N/A.
 * One function for the table cell, the column filter and the PDF, so the three
 * can't disagree. `muted` and `title` are for the table cell.
 */
export function timeToConnectCell(row) {
    if (hasMinutes(row?.time_to_connect)) {
        return { text: String(row.time_to_connect), muted: false, title: undefined };
    }
    if (row?.intercom_used) {
        return { text: TIME_TO_CONNECT_NO_ANSWER, muted: true, title: 'An intercom call was placed but nobody answered' };
    }
    return { text: TIME_TO_CONNECT_NA, muted: true, title: 'No intercom call on this alert' };
}

/** The column filter's value for a row: the same text the cell shows. */
export function timeToConnectFilterValue(row) {
    return timeToConnectCell(row).text;
}

/**
 * An Avg Time to Connect figure (headline card or one hour): '-' when it wasn't
 * computed (keys absent: the connect lookup failed), N/A when there were no
 * answered intercom alerts (present but null), else the minutes.
 */
export function formatAvgTimeToConnect(computed, value) {
    if (!computed) return '-';
    return value == null || value === '' ? TIME_TO_CONNECT_NA : value;
}

/** Display label for a row's intercom_used. */
export function intercomUsedLabel(used) {
    if (used === true) return 'Yes';
    if (used === false) return 'No';
    return '';
}

/** CSV "Intercom Answered": Yes / No when the intercom was used, blank when it wasn't. */
export function intercomAnsweredLabel(row) {
    if (!row?.intercom_used) return '';
    return hasMinutes(row.time_to_connect) ? 'Yes' : 'No';
}
